import React, { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ArrowUpRight, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import CompanyLogo from '../common/CompanyLogo';
import { buildPageList } from '../../utils/gmpBoard';
import { formatFilingDate } from '../../utils/upcomingIpos';
import { DOC_BADGE, SEBI_TABS, sebiStatusMeta } from '../../utils/sebiFilings';
import { selectCompanyLogos } from '../../store/selectors/companyLogos';

// Rows shown per page; the table has no size picker
export const PAGE_SIZE = 10;
const COLUMN_COUNT = 7;

const Dash = () => <span className="text-slate-300">—</span>;

const PageButton = ({ children, ...props }) => (
  <button
    type="button"
    className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
    {...props}
  >
    {children}
  </button>
);

/**
 * The filings table: board tabs, search, ten rows a page. Every row opens the
 * filing page for its tracker record.
 */
export const SebiFilingsTable = forwardRef(
  ({ rows, tab, onTabChange, tabCounts, query, onQueryChange, page, onPageChange, isLoading, emptyMessage }, ref) => {
    // Logos only arrive with extracted filings; any the store already holds are reused
    const logos = useSelector(selectCompanyLogos);

    const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages);
    const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    const firstIndex = rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
    const lastIndex = Math.min(currentPage * PAGE_SIZE, rows.length);

    return (
      <div ref={ref} className="scroll-mt-20 space-y-3">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div role="group" aria-label="Filing stage" className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200/80 overflow-x-auto scrollbar-none max-w-full">
            {SEBI_TABS.map((option) => {
              const active = option.key === tab;
              return (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onTabChange(option.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all ${
                    active ? 'bg-[#008080] text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {option.label} ({tabCounts[option.key] || 0})
                </button>
              );
            })}
          </div>

          <label className="relative block w-full md:w-72">
            <span className="sr-only">Search filings by company</span>
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Search company..."
              className="w-full pl-8 pr-8 py-2 bg-white border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder-slate-400 focus:outline-none focus:border-[#008080] focus:ring-1 focus:ring-[#008080]"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => onQueryChange('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-slate-700"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </label>
        </div>

        <section aria-label="SEBI filings and approvals" className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="overflow-x-auto scrollbar-slim">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  <th scope="col" className="sticky left-0 z-10 bg-slate-50 py-3 pl-4 sm:pl-5 pr-3 min-w-[240px]">
                    Company
                  </th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Document</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">SEBI Stage</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">DRHP Added</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">RHP Recorded</th>
                  <th scope="col" className="py-3 px-3 whitespace-nowrap">Analysis</th>
                  <th scope="col" className="py-3 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">Filing</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs sm:text-[13px]">
                {isLoading &&
                  Array.from({ length: 8 }).map((_, idx) => (
                    <tr key={`sebi-skel-${idx}`} className="animate-pulse">
                      <td className="sticky left-0 z-10 bg-white py-3.5 pl-4 sm:pl-5 pr-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-200" />
                          <div className="w-44 h-4 bg-slate-200 rounded" />
                        </div>
                      </td>
                      {Array.from({ length: COLUMN_COUNT - 1 }).map((__, col) => (
                        <td key={col} className="py-3.5 px-3">
                          <div className="w-14 h-4 bg-slate-100 rounded" />
                        </td>
                      ))}
                    </tr>
                  ))}

                {!isLoading && pageRows.length === 0 && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className="py-12 text-center text-sm text-slate-500">
                      {emptyMessage}
                    </td>
                  </tr>
                )}

                {!isLoading &&
                  pageRows.map((row) => {
                    const stage = sebiStatusMeta(row.status);
                    return (
                      <tr key={row.id} className="group hover:bg-slate-50/70 transition-colors">
                        <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 py-3.5 pl-4 sm:pl-5 pr-3">
                          <div className="flex items-center gap-3">
                            <CompanyLogo company={row.company} logoUrl={logos.lookup(row.company)} size="sm" maxInitials={1} />
                            <div className="min-w-0">
                              <Link to={row.detailPath} className="font-bold text-slate-900 hover:text-[#008080] leading-snug">
                                {row.company}
                              </Link>
                              <p className="text-[11px] text-slate-400">
                                {[row.board === 'sme' ? 'SME' : row.board === 'mainboard' ? 'Mainboard' : null, row.exchange]
                                  .filter(Boolean)
                                  .join(' • ') || 'Exchange not stated'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded border text-[10px] font-extrabold ${DOC_BADGE[row.docType]}`}>
                            {row.docType}
                          </span>
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-md border text-[11px] font-bold ${stage.badge}`}>{stage.label}</span>
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap text-slate-700 tabular-nums">
                          {row.addedOn ? formatFilingDate(row.addedOn) : <Dash />}
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap text-slate-700 tabular-nums">
                          {row.rhpOn ? formatFilingDate(row.rhpOn) : <Dash />}
                        </td>
                        <td className="py-3.5 px-3 whitespace-nowrap">
                          {row.analysisReady ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
                              Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-400" aria-hidden="true" />
                              In review
                            </span>
                          )}
                        </td>
                        <td className="py-3.5 pl-3 pr-4 sm:pr-5 text-right whitespace-nowrap">
                          <Link
                            to={row.detailPath}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[11px] font-bold text-slate-600 hover:border-teal-200 hover:text-[#008080]"
                          >
                            View
                            <ArrowUpRight className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-t border-slate-200/80 bg-slate-50/70">
            <span className="text-xs text-slate-500">
              {isLoading ? 'Loading filings…' : `Showing ${firstIndex} to ${lastIndex} of ${rows.length} entries`}
            </span>
            {totalPages > 1 && (
              <nav className="flex flex-wrap items-center justify-center gap-1" aria-label="Filings pagination">
                <PageButton onClick={() => onPageChange(1)} disabled={currentPage <= 1}>
                  First
                </PageButton>
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                  aria-label="Previous page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {buildPageList(currentPage, totalPages).map((p, i) =>
                  p === '…' ? (
                    <span key={`gap-${i}`} className="w-7 text-center text-xs text-slate-400">
                      …
                    </span>
                  ) : (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onPageChange(p)}
                      aria-current={p === currentPage ? 'page' : undefined}
                      className={`w-8 h-8 rounded-lg text-xs font-bold border ${
                        p === currentPage ? 'bg-[#008080] border-[#008080] text-white' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  aria-label="Next page"
                  className="w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 flex items-center justify-center"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <PageButton onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages}>
                  Last
                </PageButton>
              </nav>
            )}
          </div>
        </section>
      </div>
    );
  }
);

SebiFilingsTable.displayName = 'SebiFilingsTable';

export default SebiFilingsTable;
